import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, View, Platform, useWindowDimensions } from 'react-native'
import { DeleteModal, Header, Icon, Screen, SearchBar, Text, FeatureTooltip } from '@components'
import { useNavigation, useTheme } from '@react-navigation/native'
import { MergeContactsProps } from './types';
import { getCommonStyles, showErrorToast, showSuccessToast } from '@utils'
import { getCommonContactsStyles, getMergeContactsStyles } from './styles';
import { ProfileImage } from '../../../screens/profile/components';
import ButtonSwipe from './components/ButtonSwipe';
import { useScreen } from '@hooks';
import { useLanguage } from '@context'
import { useFtMergeSuggestions, useFtMergeContacts, useFtMergeGhostProfiles } from '@hooks';
import type { MergeSuggestion, MergeSuggestionProfile, MergeMatchReason } from '@hooks';
import TinderCard from "react-tinder-card";

// Conditional imports for mobile-only features
let Carousel: any = null
let ICarouselInstance: any = null
let Gesture: any = null
let GestureDetector: any = null
let runOnJS: any = null

if (Platform.OS !== 'web') {
  try {
    const carouselModule = require('react-native-reanimated-carousel')
    const gestureModule = require('react-native-gesture-handler')
    const reanimatedModule = require('react-native-reanimated')

    Carousel = carouselModule.default
    ICarouselInstance = carouselModule.ICarouselInstance
    Gesture = gestureModule.Gesture
    GestureDetector = gestureModule.GestureDetector
    runOnJS = reanimatedModule.runOnJS
  } catch (error) {
    console.warn('Mobile carousel dependencies not available:', error)
  }
}

const MiniProfile = ({profile, isGhost = false, onPress}: {profile: MergeSuggestionProfile, isGhost?: boolean, onPress?: () => void}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const containerRef = useRef<View>(null)
  const mouseDownPos = useRef({ x: 0, y: 0 })

  // On web, TinderCard captures mouse events and prevents normal click handling.
  // We attach a native DOM click listener directly to bypass this.
  // mousedown is NOT stopped so TinderCard can detect drag; we distinguish
  // a click from a drag by checking how far the mouse moved.
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !onPress || !containerRef.current) return
    const node = containerRef.current as unknown as HTMLElement
    if (!node?.addEventListener) return

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY }
    }
    const handleClick = (e: MouseEvent) => {
      const dx = Math.abs(e.clientX - mouseDownPos.current.x)
      const dy = Math.abs(e.clientY - mouseDownPos.current.y)
      if (dx > 5 || dy > 5) return // was a drag, ignore
      e.stopPropagation()
      onPress()
    }

    node.addEventListener('mousedown', handleMouseDown)
    node.addEventListener('click', handleClick)
    return () => {
      node.removeEventListener('mousedown', handleMouseDown)
      node.removeEventListener('click', handleClick)
    }
  }, [onPress])

  return (
    <View
      ref={containerRef}
      style={{
        alignItems: 'center',
        gap: theme.spacing.sm,
        flex: 1,
        cursor: onPress ? 'pointer' : undefined,
      } as any}
    >
      <ProfileImage isSmall isGhost={isGhost} avatarUrl={profile?.avatarUrl} />
      <Text type='bodyBold' center numberOfLines={2}>{profile.fullName}</Text>
      <View style={{
        backgroundColor: isGhost ? theme.colors.secondary + '18' : theme.colors.primary + '18',
        borderRadius: theme.border.jumbo,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xxs,
      }}>
        <Text type='bodyExtraSmall' color={isGhost ? theme.colors.secondary : theme.colors.primary}>
          {isGhost ? t('mergeSuggestions.ghostBadge') : t('mergeSuggestions.realBadge')}
        </Text>
      </View>
    </View>
  );
}

const MatchReasons = ({reasons, score}: {reasons: MergeMatchReason[], score: number}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  return (
    <View style={{
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
      justifyContent: 'center',
    }}>
      {reasons.map((reason, index) => (
        <View key={index} style={{
          backgroundColor: score >= 80 ? theme.colors.green + '20' : theme.colors.primary + '15',
          borderRadius: theme.border.jumbo,
          paddingHorizontal: theme.spacing.md,
          paddingVertical: theme.spacing.xxs + 1,
        }}>
          <Text type='bodyExtraSmall' color={score >= 80 ? theme.colors.green : theme.colors.primary}>{t(`mergeSuggestions.reason.${reason}`)}</Text>
        </View>
      ))}
    </View>
  )
}

const SuggestionCard = ({suggestion, onProfilePress}: {suggestion: MergeSuggestion, onProfilePress?: (profileId: string) => void}) => {
  const theme = useTheme()

  return (
    <View style={{gap: theme.spacing.lg, paddingVertical: theme.spacing.sm}}>
      <View style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing.md,
      }}>
        <MiniProfile
          profile={suggestion.ghostProfile}
          isGhost
          onPress={onProfilePress ? () => onProfilePress(suggestion.ghostProfile.id!) : undefined}
        />
        <View style={{justifyContent: 'center', paddingTop: theme.spacing.xxxl}}>
          <Icon name='arrowRight' size={theme.iconSize.large} color={theme.colors.primary} />
        </View>
        <MiniProfile
          profile={suggestion.realContact}
          isGhost={suggestion.isGhostPair}
          onPress={onProfilePress ? () => onProfilePress(suggestion.realContact.accountId!) : undefined}
        />
      </View>
      <MatchReasons reasons={suggestion.matchReasons} score={suggestion.matchScore} />
    </View>
  )
}

export const MergeContacts: React.FC<MergeContactsProps> = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const carouselHeight = Math.round(
    Math.min(Math.max(windowHeight * 0.48, 380), windowHeight - 220),
  )
  const { isWeb, isMobile, isDesktop } = useScreen()
  const styles = getMergeContactsStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const commonContactsStyles = getCommonContactsStyles(theme)

  const [searchText, setSearchText] = useState('')
  const [removedIds, setRemovedIds] = useState<string[]>([])

  // A merge cannot be undone: the suggestion waits here until the user
  // answers the confirmation dialog.
  const [pendingMerge, setPendingMerge] = useState<MergeSuggestion | null>(null)

  const { data: suggestions = [], isLoading, isError, keepsGhostDetails } = useFtMergeSuggestions()
  // Name of the contact each running merge goes to, for its success message.
  const mergeTargetNamesRef = useRef<Record<string, string>>({})
  // Reported per merge: several can be running at once, and one can answer
  // after the screen was left.
  const mergeMutation = useFtMergeContacts({
    onSuccess: (res, { ghostProfileId }) =>
      showSuccessToast(
        t('mergeSuggestions.done'),
        res?.detailsNoteId
          ? t('mergeSuggestions.doneNote', { name: mergeTargetNamesRef.current[ghostProfileId] })
          : undefined,
      ),
    onError: (_error, { ghostProfileId }) => {
      // The card was hidden when the merge started: bring it back.
      setRemovedIds((prev) => prev.filter((id) => id !== ghostProfileId))
      showErrorToast(t('mergeSuggestions.failed'))
    },
  })

  // Two cards of the same person: the duplicate goes into the kept card.
  const mergeGhostPairMutation = useFtMergeGhostProfiles({
    onSuccess: (res, { duplicateGhostProfileId }) =>
      showSuccessToast(
        t('mergeSuggestions.done'),
        res?.detailsNoteId
          ? t('mergeSuggestions.doneGhostNote', { name: mergeTargetNamesRef.current[duplicateGhostProfileId] })
          : undefined,
      ),
    onError: (_error, { duplicateGhostProfileId }) => {
      setRemovedIds((prev) => prev.filter((id) => id !== duplicateGhostProfileId))
      showErrorToast(t('mergeSuggestions.failed'))
    },
  })

  const runMerge = (suggestion: MergeSuggestion) => {
    const ghostId = suggestion.ghostProfile.id
    mergeTargetNamesRef.current[ghostId] = suggestion.realContact.fullName
    if (suggestion.isGhostPair) {
      mergeGhostPairMutation.mutate({
        keptGhostProfileId: suggestion.realContact.accountId,
        duplicateGhostProfileId: ghostId,
      })
      return
    }
    mergeMutation.mutate({ ghostProfileId: ghostId, realContactAccountId: suggestion.realContact.accountId })
  }

  const confirmMerge = (suggestion: MergeSuggestion) => {
    setRemovedIds((prev) => (prev.includes(suggestion.ghostProfile.id) ? prev : [...prev, suggestion.ghostProfile.id]))
    runMerge(suggestion)
  }

  const filteredSuggestions = useMemo(() => {
    return suggestions
      .filter(s => !removedIds.includes(s.ghostProfile.id))
      .filter(s => {
        if (!searchText) return true
        const search = searchText.toLowerCase()
        return (
          s.ghostProfile.fullName?.toLowerCase().includes(search) ||
          s.realContact.fullName?.toLowerCase().includes(search)
        )
      })
  }, [suggestions, removedIds, searchText])

  const [currentIndex, setCurrentIndex] = useState(filteredSuggestions.length - 1)

  React.useEffect(() => {
    setCurrentIndex(filteredSuggestions.length - 1)
  }, [filteredSuggestions.length])

  const ref = React.useRef<any>(null);
  const nativeCarouselIndexRef = useRef(0);
  const currentIndexRef = useRef(currentIndex)
  // Ghost id of the card swiped right on web and waiting for the dialog.
  const swipedToConfirmRef = useRef<string | null>(null)

  const childRefs = useMemo(
    () =>
      Array(filteredSuggestions.length)
        .fill(null)
        .map(() => React.createRef()),
    [filteredSuggestions.length]
  )

  const canGoBack = currentIndex < filteredSuggestions.length - 1
  const canSwipe = currentIndex >= 0

  const rollback = async () => {
    if(!canGoBack) return
    const newIndex = currentIndex + 1
    updateCurrentIndex(newIndex)
    setRemovedIds(prev => prev.slice(0, -1))
    // @ts-ignore
    await childRefs[newIndex].current?.restoreCard()
  }

  const swipe = async (dir: 'left' | 'right') => {
    if(!canSwipe || currentIndex >= filteredSuggestions.length) return
    // @ts-ignore
    await childRefs[currentIndex].current?.swipe(dir)
  }

  const updateCurrentIndex = (val: number) => {
    setCurrentIndex(val)
    currentIndexRef.current = val
  }

  const handleSwipe = (direction: string, suggestion: MergeSuggestion, idx: number) => {
    if (direction === 'right') {
      // Nothing is merged or hidden yet: once the card has slid out,
      // outOfFrame puts it back behind the confirmation dialog.
      swipedToConfirmRef.current = suggestion.ghostProfile.id
      setPendingMerge(suggestion)
      return
    }
    setRemovedIds(prev => [...prev, suggestion.ghostProfile.id])
    updateCurrentIndex(idx - 1)
  }

  // Web "Fusionner" button: ask first, without sliding the card away.
  const askMergeTopCard = () => {
    const suggestion = filteredSuggestions[currentIndex]
    if (suggestion) setPendingMerge(suggestion)
  }

  const outOfFrame = (name: string, idx: number) => {
    if (swipedToConfirmRef.current === name) {
      swipedToConfirmRef.current = null
      // @ts-ignore
      childRefs[idx]?.current?.restoreCard()
      return
    }
    // @ts-ignore
    currentIndexRef.current >= idx && childRefs[idx].current?.restoreCard()
  }

  /** Native: cancel = swipe left (scroll to next page), merge = swipe right; same skip/merge as web.
   * Uses scrollTo(index±1) instead of next()/prev() because next() can return early (overscroll guard)
   * without calling onFinished — so "Annuler" would only work once. */
  const handleNativeSwipeButton = useCallback(
    (direction: 'left' | 'right', confirmed?: MergeSuggestion) => {
      if (Platform.OS === 'web' || !ref.current) return;
      const list = filteredSuggestions;
      if (!list.length) return;
      const shown =
        typeof ref.current.getCurrentIndex === 'function'
          ? ref.current.getCurrentIndex()
          : nativeCarouselIndexRef.current;
      // A confirmed merge is the one the dialog named, wherever that card is
      // now: the carousel may have moved, or the list changed, meanwhile.
      const i = confirmed
        ? list.findIndex((s) => s.ghostProfile.id === confirmed.ghostProfile.id)
        : shown;
      if (i < 0) return;
      nativeCarouselIndexRef.current = i;
      const suggestion = list[i];
      if (!suggestion?.ghostProfile?.id) return;

      // A merge (button or right swipe) only runs once the dialog has confirmed it.
      if (direction === 'right' && !confirmed) {
        setPendingMerge(suggestion);
        return;
      }

      const ghostId = suggestion.ghostProfile.id;
      const oldLen = list.length;

      const apply = () => {
        setRemovedIds((prev) => (prev.includes(ghostId) ? prev : [...prev, ghostId]));
        if (direction === 'right') {
          runMerge(suggestion);
        }
        const newLen = oldLen - 1;
        if (newLen > 0) {
          const targetIndex = Math.min(i, newLen - 1);
          requestAnimationFrame(() => {
            ref.current?.scrollTo?.({ index: targetIndex, animated: false });
            nativeCarouselIndexRef.current = targetIndex;
          });
        }
      };

      if (direction === 'left') {
        if (i >= oldLen - 1) {
          apply();
        } else {
          ref.current.scrollTo?.({ index: i + 1, animated: true, onFinished: apply });
        }
      } else if (i <= 0 || i !== shown) {
        apply();
      } else {
        ref.current.scrollTo?.({ index: i - 1, animated: true, onFinished: apply });
      }
    },
    [filteredSuggestions, mergeMutation],
  );

  const onMergePanEnd = useCallback(
    (translationX: number, velocityX: number) => {
      const minDistance = 72;
      const minSpeed = 520;
      if (translationX < -minDistance || velocityX < -minSpeed) {
        handleNativeSwipeButton('left');
      } else if (translationX > minDistance || velocityX > minSpeed) {
        handleNativeSwipeButton('right');
      }
    },
    [handleNativeSwipeButton],
  );

  const pan =
    Platform.OS !== 'web' && Gesture && runOnJS
      ? Gesture.Pan()
          .activeOffsetX([-24, 24])
          .onEnd((e: { translationX: number; velocityX?: number }) => {
            'worklet';
            runOnJS(onMergePanEnd)(e.translationX, e.velocityX ?? 0);
          })
      : null;

  const navigation = useNavigation()

  const handleProfilePress = useCallback((profileId: string) => {
    ;(navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId },
    })
  }, [navigation])

  const remainingCount = filteredSuggestions.length

  return (
    <Screen>
      <Header
        title={t('contactsScreen.mergeProfiles')}
        iconLeft={isWeb? undefined : "arrowBack"}
        iconColor={theme.colors.secondary}
      />

      <FeatureTooltip
        id="merge_intro"
        title={t('featureTooltip.merge_intro.title')}
        description={t('featureTooltip.merge_intro.description')}
        icon="merge"
      />

      <View style={!isMobile && styles.searchContainerNonMobile}>
        <SearchBar
          value={searchText}
          onChangeText={setSearchText}
          containerStyle={[commonContactsStyles.searchbar, isMobile ? commonContactsStyles.searchbarMobile : commonStyles.searchContainerMaxWidth]}
        />
      </View>

      {isLoading && (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md}}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      )}

      {isError && (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', padding: theme.spacing.lg}}>
          <Text type='body' color={theme.colors.error}>{t('errors.title')}</Text>
        </View>
      )}

      {!isLoading && !isError && (
        <>
          <View style={commonContactsStyles.row}>
            <View style={commonContactsStyles.roundedIcon}>
              <Icon name='refresh2' color={theme.colors.primary} onPress={() => isWeb ? rollback() : ref.current?.prev()}/>
            </View>
            <View style={commonContactsStyles.flexShadowText}>
              <Text type='bodyBold' color={theme.colors.primary}>
                {remainingCount > 0
                  ? `${remainingCount} ${remainingCount > 1 ? t('contactsScreen.profilesRemainingToMerge') : t('contactsScreen.profileRemainingToMerge')}`
                  : t('contactsScreen.noProfileToMerge')}
              </Text>
            </View>
            <View style={commonContactsStyles.roundedIcon}>
              <Icon name='next' color={theme.colors.primary} onPress={() => isWeb ? swipe('left') : ref.current?.next()}/>
            </View>
          </View>

          {isWeb ? (
            <View style={[commonContactsStyles.container, {marginTop: theme.spacing.md}]}>
              {filteredSuggestions.length > 0 && <View style={styles.containerCard}>
                {isDesktop && remainingCount > 0 && <ButtonSwipe text={t('common.cancel')} onPress={() => swipe('left')}/>}
                <View style={[styles.profileCardContainer, {maxWidth: Math.min(500, windowWidth - 60)}]}>
                  {filteredSuggestions.map((suggestion, index) => {
                    const baseTranslateX = index % 3 === 0 ? 5 : (index % 3 === 1 ? 0 : -5);

                    return (
                      <TinderCard
                        // @ts-ignore
                        ref={childRefs[index]}
                        key={suggestion.ghostProfile.id}
                        className='swipe-card'
                        onSwipe={(dir) => handleSwipe(dir, suggestion, index)}
                        onCardLeftScreen={() => outOfFrame(suggestion.ghostProfile.id, index)}
                        preventSwipe={['up', 'down']}
                        // Trigger on how far the card is dragged, not how fast:
                        // a calm slide past ~110px counts, instead of needing a
                        // quick flick (the library's velocity default).
                        swipeRequirementType="position"
                        swipeThreshold={110}
                      >
                        <View style={[styles.card, {
                          transform: `translateX(${baseTranslateX}px)`,
                          padding: theme.spacing.xl,
                          userSelect: 'none',
                        } as any]}>
                          <SuggestionCard suggestion={suggestion} onProfilePress={handleProfilePress} />
                        </View>
                      </TinderCard>
                    );
                  })}
                </View>
                {!isDesktop && <View style={commonContactsStyles.buttonsContainer}>
                  <ButtonSwipe text={t('common.cancel')} onPress={() => swipe('left')}/>
                  <ButtonSwipe text={t('contactsScreen.merge')} isRight onPress={askMergeTopCard}/>
                </View>}
                {isDesktop && remainingCount > 0 && <ButtonSwipe text={t('contactsScreen.merge')} isRight onPress={askMergeTopCard}/>}
              </View>}
            </View>
          ) : (
            <GestureDetector gesture={pan}>
              <View style={commonContactsStyles.container}>
                <View style={styles.containerCard}>
                  <Carousel
                    ref={ref}
                    data={filteredSuggestions}
                    enabled={false}
                    loop={true}
                    autoFillData={false}
                    defaultIndex={0}
                    width={windowWidth}
                    height={carouselHeight}
                    onSnapToItem={(idx: number) => {
                      nativeCarouselIndexRef.current = idx;
                    }}
                    renderItem={({ item }: { item: MergeSuggestion; index: number }) => (
                      <View
                        style={[
                          styles.card,
                          {
                            marginHorizontal: theme.spacing.md,
                            flex: 1,
                            borderColor: theme.colors.border,
                            borderWidth: 1,
                            justifyContent: 'center',
                            minHeight: carouselHeight - theme.spacing.lg * 2,
                          },
                        ]}
                      >
                        <SuggestionCard suggestion={item} onProfilePress={handleProfilePress} />
                      </View>
                    )}
                  />
                  <View style={commonContactsStyles.buttonsContainer}>
                    <ButtonSwipe
                      text={t('common.cancel')}
                      onPress={() => handleNativeSwipeButton('left')}
                    />
                    <ButtonSwipe
                      text={t('contactsScreen.merge')}
                      isRight
                      onPress={() => handleNativeSwipeButton('right')}
                    />
                  </View>
                </View>
              </View>
            </GestureDetector>
          )}
          <View style={[commonStyles.paddingHorizontal, {marginBottom: theme.spacing.xxl}]}>
            <Text type='bodySmall' center color={theme.colors.secondary}>
              {t('contactsScreen.swipeToMerge')}
            </Text>
          </View>
        </>
      )}

      <DeleteModal
        isVisible={!!pendingMerge}
        setIsVisible={(visible) => { if (!visible) setPendingMerge(null) }}
        title=""
        heading={t('mergeSuggestions.confirmTitle')}
        description={pendingMerge
          ? pendingMerge.isGhostPair
            ? t('mergeSuggestions.confirmGhostPair', {
                ghost: pendingMerge.ghostProfile.fullName,
                kept: pendingMerge.realContact.fullName,
              })
            : t(keepsGhostDetails ? 'mergeSuggestions.confirmKept' : 'mergeSuggestions.confirmLost', {
                ghost: pendingMerge.ghostProfile.fullName,
                real: pendingMerge.realContact.fullName,
              })
          : ''}
        deleteLabel={t('contactsScreen.merge')}
        onDelete={() => {
          if (!pendingMerge) return
          if (Platform.OS === 'web') confirmMerge(pendingMerge)
          else handleNativeSwipeButton('right', pendingMerge)
        }}
      />
    </Screen>
  )
}
